import { useEffect, useState } from 'react';
import { Button } from '@siliconx/shared/ui/Button';
import { api } from '../lib/api';

/**
 * SIL-1898. Model picker for hardware sitting in Customs. Shows the matcher's
 * ranked candidates for the server or GPU, a search box over the same
 * catalogue pool for when the ranker found nothing, and verifies the asset
 * against the chosen model. Without this, a probe asset the ingest-time matcher
 * could not match had no way out of `unmatched`.
 */

export interface CustomsSkuCandidate {
  skuId: string;
  verifiedSku: string;
  modelName: string | null;
}

export function CustomsSkuPicker({
  assetId,
  busy,
  onPick,
  onCancel,
}: {
  assetId: string;
  busy: boolean;
  onPick: (skuId: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [candidates, setCandidates] = useState<CustomsSkuCandidate[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setCandidates(null);
    setError(false);
    void (async () => {
      try {
        const res = await api.api.client.inventory.customs[':assetId']['sku-candidates'].$get({
          param: { assetId },
          query: debounced ? { q: debounced } : {},
        });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { candidates: CustomsSkuCandidate[] };
        if (!cancelled) setCandidates(body.candidates);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId, debounced]);

  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="mb-2 text-xs text-muted-foreground">
        {debounced ? `Catalogue models matching “${debounced}”.` : 'Best catalogue matches for what this hardware reported.'}{' '}
        Your pick is recorded as your choice and reviewed by SiliconX.
      </p>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search models, e.g. H100 80GB SXM"
          className="min-w-[18rem] rounded-md border border-border bg-background px-2 py-1 text-sm"
          aria-label="Search catalogue models"
        />
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Close
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-red-600">Could not load models. Try again.</p>
      ) : candidates === null ? (
        <p className="text-xs text-muted-foreground">Loading models…</p>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No catalogue model found. Try a different search. If this model is missing from the catalogue, contact
          support to have it added.
        </p>
      ) : (
        <ul className="space-y-1">
          {candidates.map((c) => (
            <li key={c.skuId} className="flex items-center justify-between gap-3 rounded border border-border px-2 py-1">
              <div className="min-w-0">
                <p className="truncate text-sm">{c.modelName ?? c.verifiedSku}</p>
                <p className="truncate font-mono text-2xs text-muted-foreground">{c.verifiedSku}</p>
              </div>
              <Button size="sm" disabled={busy} onClick={() => onPick(c.skuId)}>
                Use this model
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
